'use memo';

import {
  AutopilotTaskDto,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useToggleTaskSteps,
} from '@urbanx/agx.activities.client';
import {
  AgxAnimatedToggleLoaderOverlay,
  AgxAnimatedToggleLoaderState,
  type AgxAnimatedToggleLoaderStateValue,
  AgxAvatar,
  AgxLoadingState,
  AgxModal,
  AgxSpacer,
  AgxStepsIndicator,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import TaskModalActionButtons from './TaskModalActionButtons';
import { CampaignDetailModel, DocumentGroup } from '@urbanx/agx.campaigns.client';
import TasksFilesList from 'pages/campaigns/components/campaignTasks/TasksFilesList';
import { TaskModalPageProps } from './TaskModalPageProps';
import { emitTaskUpdate, useAutopilotTaskUpdates } from 'hooks/useAutopilotTaskUpdates';
import { applyTaskUpdateToTasks } from 'utils/tasks';
import { TaskUpdatedNotification } from 'types/pushNotifications';
import TaskDueDateBadge from 'components/tasks/TaskDueDateBadge';
import {
  isWorkflowConcurrencyError,
  useWorkflowConcurrencyError,
} from 'hooks/useWorkflowConcurrencyError';
import './TaskModalDialog.scss';

export interface TaskModalContentConfig {
  documents: DocumentGroup[];
  isPending?: boolean;
  onSaveCurrentPage?: (task: AutopilotTaskDto) => void | Promise<void>;
  onSaveAllPages?: (tasks: AutopilotTaskDto[]) => void | Promise<void>;
  renderPage: (props: TaskModalPageProps) => React.ReactNode;
  onCompleteSuccess?: () => void;
  completeDisabled?: boolean;
  saveAndExitDisabled?: boolean;
  initialPageIndex?: number;
  modalId?: string;
  flow?: TaskModalFlow;
  hideTaskMetadata?: boolean;
  hideCloseButton?: boolean;
  previousButtonText?: string;
  presentationClassName?: string;
}

export interface TaskModalFlow {
  resolvePageTasks: (tasks: AutopilotTaskDto[]) => AutopilotTaskDto[];
  resolvePersistenceTasks: (tasks: AutopilotTaskDto[]) => AutopilotTaskDto[];
  canComplete: (tasks: AutopilotTaskDto[]) => boolean;
}

const areAllTaskStepsCompleted = (tasks: AutopilotTaskDto[]) =>
  tasks.every(
    (task) =>
      (task.taskSteps?.length ?? 0) > 0 && (task.taskSteps ?? []).every((step) => step.isCompleted)
  );

const defaultTaskModalFlow: TaskModalFlow = {
  resolvePageTasks: (tasks) => tasks,
  resolvePersistenceTasks: (tasks) => tasks,
  canComplete: areAllTaskStepsCompleted,
};

interface TaskModalDialogProps {
  title: string;
  tasks: AutopilotTaskDto[];
  open: boolean;
  onClose: () => void;
  campaignId: string;
  campaign: CampaignDetailModel;
  contentConfig: TaskModalContentConfig;
}

const TaskModalDialog = ({
  title,
  tasks,
  open,
  onClose,
  campaignId,
  campaign,
  contentConfig,
}: TaskModalDialogProps) => {
  const {
    documents,
    isPending = false,
    onSaveCurrentPage,
    onSaveAllPages,
    renderPage,
    onCompleteSuccess,
    completeDisabled = false,
    saveAndExitDisabled = false,
    initialPageIndex = 0,
    modalId,
    flow = defaultTaskModalFlow,
    hideTaskMetadata = false,
    hideCloseButton = false,
    previousButtonText = 'Back',
    presentationClassName,
  } = contentConfig;
  const [currentPage, setCurrentPage] = useState(0);
  const [localTasks, setLocalTasks] = useState<AutopilotTaskDto[]>(tasks);
  const [isPersistingCurrentPage, setIsPersistingCurrentPage] = useState(false);
  const isPersistingCurrentPageRef = useRef(false);
  const [completionLoaderState, setCompletionLoaderState] =
    useState<AgxAnimatedToggleLoaderStateValue>();
  const beforeTaskModalSaveRef = useRef<(() => boolean | Promise<boolean>) | undefined>(undefined);
  const taskModalPageValidationRef = useRef<(() => boolean | Promise<boolean>) | undefined>(
    undefined
  );
  const persistedTasksRef = useRef<AutopilotTaskDto[]>(tasks);
  const persistingTaskRef = useRef<AutopilotTaskDto | undefined>(undefined);
  const renderedPageRef = useRef<React.ReactNode>(null);
  const wasOpenRef = useRef(open);
  const queryClient = useQueryClient();
  const initialPageTasks = flow.resolvePageTasks(tasks);
  const resolvedInitialPageIndex = Math.min(
    Math.max(initialPageIndex, 0),
    Math.max(initialPageTasks.length - 1, 0)
  );

  useEffect(() => {
    if (!open) {
      setLocalTasks(tasks);
      persistedTasksRef.current = tasks;
      setCurrentPage(resolvedInitialPageIndex);
    }
  }, [tasks, open, resolvedInitialPageIndex]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setLocalTasks(tasks);
      persistedTasksRef.current = tasks;
      setCurrentPage(resolvedInitialPageIndex);
    }

    wasOpenRef.current = open;
  }, [open, tasks, resolvedInitialPageIndex]);

  const pageTasks = flow.resolvePageTasks(localTasks);
  const currentTask = pageTasks[currentPage];
  const titleTask = currentTask ?? pageTasks[0];

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();

  const { pushToast } = useToast();
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError();

  const applyTaskUpdate = (taskUpdate: TaskUpdatedNotification) => {
    persistedTasksRef.current = applyTaskUpdateToTasks(persistedTasksRef.current, taskUpdate);
    if (isPersistingCurrentPageRef.current) return;

    setLocalTasks((prevTasks) => applyTaskUpdateToTasks(prevTasks, taskUpdate));
  };

  useAutopilotTaskUpdates(applyTaskUpdate);

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: (updatedTask) => {
      if (!updatedTask) return;

      queryClient.setQueryData<AutopilotTaskDto[]>(
        ['getAutopilotTasksForCampaign', campaignId, updatedTask.contractId ?? undefined],
        (previousTasks) =>
          previousTasks
            ? applyTaskUpdateToTasks(previousTasks, {
                task: updatedTask,
                collectionStateChanged: false,
              })
            : previousTasks
      );
      emitTaskUpdate({ task: updatedTask, collectionStateChanged: false });
    },
    onError: async (error) => {
      if (
        persistingTaskRef.current &&
        (await handleWorkflowConcurrencyError(error, persistingTaskRef.current, {
          restore: () => setLocalTasks(persistedTasksRef.current),
        }))
      ) {
        return;
      }

      pushToast({
        type: 'error',
        message: 'Failed to toggle task steps',
      });
    },
  });

  const toggleTaskStep = (stepOrder: number, isChecked: boolean) => {
    if (!currentTask?.id) return;

    toggleTaskStepForTask(currentTask.id, stepOrder, isChecked);
  };

  const toggleTaskStepForTask = (taskId: string, stepOrder: number, isChecked: boolean) => {
    setLocalTasks((prev) =>
      prev.map((task) => {
        if (task.id !== taskId) {
          return task;
        }

        return {
          ...task,
          taskSteps:
            task.taskSteps?.map((step) =>
              step.order === stepOrder ? { ...step, isCompleted: isChecked } : step
            ) ?? [],
        };
      })
    );
  };

  const registerBeforeTaskModalSave = (handler: (() => boolean | Promise<boolean>) | undefined) => {
    beforeTaskModalSaveRef.current = handler;
  };

  const registerTaskModalPageValidation = (
    handler: (() => boolean | Promise<boolean>) | undefined
  ) => {
    taskModalPageValidationRef.current = handler;
  };

  const canPersistCurrentPage = async () => {
    const beforeTaskModalSave = beforeTaskModalSaveRef.current;

    if (!beforeTaskModalSave) return true;

    return await beforeTaskModalSave();
  };

  const isCurrentPageValid = async () => {
    const validateCurrentPage = taskModalPageValidationRef.current;

    if (!validateCurrentPage) return true;

    return await validateCurrentPage();
  };

  const getChangedStepIds = (task: AutopilotTaskDto | undefined) => {
    const persistedTask = persistedTasksRef.current.find((candidate) => candidate.id === task?.id);

    return (
      task?.taskSteps
        ?.filter((step) => {
          const persistedStep = persistedTask?.taskSteps?.find(
            (candidate) => candidate.order === step.order
          );

          return (persistedStep?.isCompleted ?? false) !== (step.isCompleted ?? false);
        })
        .map((step) => step.order ?? 0) ?? []
    );
  };

  const persistTaskSteps = async (task: AutopilotTaskDto | undefined) => {
    const changedStepIds = getChangedStepIds(task);

    if (changedStepIds.length === 0 || !task?.id) return;

    persistingTaskRef.current = task;
    await toggleTaskSteps({
      taskId: task.id,
      stepIds: changedStepIds,
    });
    await invalidateTasks(campaignId, task.contractId);
  };

  const persistAllPages = async () => {
    const persistenceTasks = flow.resolvePersistenceTasks(localTasks);

    if (onSaveAllPages) {
      await onSaveAllPages(persistenceTasks);
    } else if (currentTask) {
      await onSaveCurrentPage?.(currentTask);
    }

    for (const task of persistenceTasks) {
      await persistTaskSteps(task);
    }
  };

  const onPrevious = async () => {
    if (isPersistingCurrentPageRef.current) return;

    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const onNext = async () => {
    if (isPersistingCurrentPageRef.current) return;
    if (!(await isCurrentPageValid())) return;

    setCurrentPage((prev) => Math.min(prev + 1, pageTasks.length - 1));
  };

  const onSaveAndExit = async () => {
    if (isPersistingCurrentPageRef.current) return;

    isPersistingCurrentPageRef.current = true;
    setIsPersistingCurrentPage(true);
    try {
      if (!(await canPersistCurrentPage())) return;

      await persistAllPages();
      onClose();
    } catch (error) {
      if (isWorkflowConcurrencyError(error)) return;

      pushToast({
        type: 'error',
        message: 'Failed to save task changes',
      });
    } finally {
      isPersistingCurrentPageRef.current = false;
      setIsPersistingCurrentPage(false);
    }
  };

  const onComplete = async () => {
    if (isPersistingCurrentPageRef.current) return;

    isPersistingCurrentPageRef.current = true;
    setIsPersistingCurrentPage(true);
    try {
      if (!(await isCurrentPageValid())) return;
      if (!(await canPersistCurrentPage())) return;
      if (!flow.canComplete(localTasks) || completeDisabled) return;

      setCompletionLoaderState(AgxAnimatedToggleLoaderState.Loading);
      await persistAllPages();
      setCompletionLoaderState(AgxAnimatedToggleLoaderState.Complete);
      onCompleteSuccess?.();
    } catch (error) {
      setCompletionLoaderState(undefined);

      if (isWorkflowConcurrencyError(error)) return;

      pushToast({
        type: 'error',
        message: 'Failed to save task changes',
      });
    } finally {
      isPersistingCurrentPageRef.current = false;
      setIsPersistingCurrentPage(false);
    }
  };

  const onCompletionLoaderComplete = () => {
    setCompletionLoaderState(undefined);
    onClose();
  };

  const shouldRenderPage = open && currentTask && (!isPending || isPersistingCurrentPage);
  const shouldRenderLoading = open && isPending && !isPersistingCurrentPage;
  let pageContent: React.ReactNode = null;

  if (isPersistingCurrentPage) {
    pageContent = renderedPageRef.current;
  } else if (shouldRenderPage) {
    pageContent = renderPage({
      task: currentTask,
      tasks: localTasks,
      taskNumber: currentPage + 1,
      campaign,
      toggleTaskStep,
      toggleTaskStepForTask,
      registerBeforeTaskModalSave,
      registerTaskModalPageValidation,
    });
    renderedPageRef.current = pageContent;
  } else if (!open) {
    renderedPageRef.current = null;
  }

  const assigneeInitials = titleTask?.assignedTo
    ?.split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return (
    <AgxModal
      id={modalId}
      type="fullScreen"
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      setIsDialogOpen={onClose}
      isOpen={open}
      hideCloseButton={hideCloseButton}
      className={presentationClassName}
      secondaryTitle={
        <AgxSpacer direction="horizontal" contentAlignment="center" fill>
          <div className="taskModalHeader">
            <div className="taskModalHeader__title">
              <AgxTypography
                type="body_ui_strong"
                size="medium"
                id="titlemodal"
                color="var(--text-primary)"
              >
                {title}
              </AgxTypography>
              {!hideTaskMetadata && assigneeInitials && (
                <AgxAvatar size="xs" type="text-dark" text={assigneeInitials} />
              )}
              {!hideTaskMetadata && titleTask?.dueUtc && (
                <TaskDueDateBadge
                  dueDate={titleTask.dueUtc}
                  isCompleted={titleTask.isCompleted ?? false}
                />
              )}
            </div>
            <div className="taskModalHeader__steps">
              <AgxStepsIndicator totalLevels={pageTasks.length} currentLevel={currentPage + 1} />
            </div>
          </div>
        </AgxSpacer>
      }
      content={
        <>
          {(isPersistingCurrentPage || completionLoaderState) && (
            <AgxAnimatedToggleLoaderOverlay
              background="transparent"
              label={
                completionLoaderState === AgxAnimatedToggleLoaderState.Complete
                  ? 'Complete'
                  : 'Completing task...'
              }
              onComplete={onCompletionLoaderComplete}
              state={completionLoaderState ?? AgxAnimatedToggleLoaderState.Loading}
            />
          )}
          <AgxSpacer
            className="taskModalBody"
            direction="horizontal"
            padding={{
              top: 'md',
              bottom: 'md',
              left: 'xl',
              right: 'xl',
            }}
            fill
          >
            <div className="taskModalContentGrid">
              <div className="taskModalContentGrid__column taskModalContentGrid__column--files">
                <TasksFilesList files={documents} id={campaignId} />
              </div>
              <div className="taskModalContentGrid__column taskModalContentGrid__column--form">
                <AgxSpacer direction="vertical" fill>
                  {pageContent}
                  {shouldRenderLoading ? <AgxLoadingState /> : null}
                </AgxSpacer>
              </div>
            </div>
          </AgxSpacer>
        </>
      }
      actionButtons={
        <TaskModalActionButtons
          currentPage={currentPage}
          totalPages={pageTasks.length}
          onPrevious={onPrevious}
          onNext={onNext}
          onComplete={onComplete}
          onSaveAndExit={onSaveAndExit}
          completeDisabled={completeDisabled || !flow.canComplete(localTasks)}
          saveAndExitDisabled={saveAndExitDisabled}
          isSaving={isPersistingCurrentPage}
          previousButtonText={previousButtonText}
        />
      }
    />
  );
};

export default TaskModalDialog;
